import { assetsClient } from '@/clients/assets-client.js';
import { HatElement } from '@/components/hat-element.js';

class HatLocations extends HatElement {
  private _data: any[] = [];

  connectedCallback() {
    super.connectedCallback();
    this.loadData();
  }

  async loadData() {
    this._data = [];
    this._renderShell();

    try {
      let nextPageToken: string | null = null;
      let totalLoaded = 0;

      do {
        const result = await assetsClient.getLocations(nextPageToken);

        if (result.locations.length) {
          this._data.push(...result.locations);
          await this._appendCards(result.locations);
          totalLoaded += result.locations.length;
        }

        nextPageToken = result.nextPageToken;
      } while (nextPageToken);

      if (totalLoaded === 0) {
        this._showEmptyState();
      }
    } catch (err) {
      console.error(err);
      this.root.innerHTML = `<p>Error loading locations.</p>`;
    }
  }

  _renderShell() {
    this.root.innerHTML = `
      <style>
        :host { display: block; }

        .container {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .header {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        h2 { margin: 0; }

        .list {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .empty-state {
          margin-top: 12px;
          padding: 42px 28px;
          text-align: center;
          background: #fafafa;
          border: 2px dashed #dcdcdc;
          border-radius: 18px;
        }

        .empty-icon { font-size: 46px; margin-bottom: 12px; }

        .empty-title {
          font-size: 22px;
          font-weight: 600;
          color: #222;
          margin-bottom: 10px;
        }

        .empty-text {
          color: #666;
          line-height: 1.6;
          max-width: 460px;
          margin: 0 auto;
        }
      </style>

      <div class="container">
        <div class="header">
          <hat-nav-button href="/app/locations/new" hat-entitlement="feature.locations.new">New Location</hat-nav-button>
        </div>
        <div class="list"></div>
      </div>
    `;
  }

  _showEmptyState() {
    const list = this.root.querySelector('.list')!;
    list.innerHTML = `
      <div class="empty-state">
        <div class="empty-icon">🏠</div>
        <div class="empty-title">No locations yet</div>
        <div class="empty-text">
          You currently don't have any locations saved.
          Click <strong>New Location</strong> above to create your first location.
        </div>
      </div>
    `;
  }

  async _appendCards(locations: any[]) {
    const list = this.root.querySelector('.list')!;
    await customElements.whenDefined('hat-location-card');

    locations.forEach(location => {
      const card = document.createElement('hat-location-card');
      card.dataset.locationId = location.id;
      customElements.upgrade(card);
      (card as any).setData(location);
      card.addEventListener('delete', (e: Event) => {
        e.stopPropagation();
        this.removeCardById((e as CustomEvent).detail.id);
      });
      list.appendChild(card);
    });
  }

  removeCardById(id: string) {
    this._data = this._data.filter(loc => loc.id !== id);
    this.root.querySelector(`[data-location-id="${id}"]`)?.remove();
    if (this._data.length === 0) {
      this._showEmptyState();
    }
  }
}

customElements.define('hat-locations', HatLocations);